import React, { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { UserPermissions } from '~src/interfaces/session.types';

import { MatrixResponse, SegmentAndAggregations, TopRow } from '~src/interfaces/heatmap.interface';
import { Scale } from '~src/interfaces/scale.interface';
import { SegmentType } from '~src/interfaces/segment.interface';
import { ESTopic, Topic } from '~src/interfaces/topic.interface';
import { PeriodValues } from '~src/modules/reports/shared/constants/filterAndSortingKeys';
import { StoreI } from '~src/rootReducer';
import { SelectedGroupObj } from '~src/store/heatmap/heatmapReducer';

import EmptyTableInner from './EmptyTableInner';
import { RatingRow } from './ratingTable.sc';
import { EmptyContainer, MasterTilesWrapper, TableInner } from './ratingTableInner.sc';
import EmptyRow from './row/EmptyRow';
import TilesRow from './row/TilesRow';
import EmptyTile from './tile/EmptyTile';

interface OwnProps {
  selectedTopicId: string | null;
  selectedPeriod: PeriodValues;
  filterSurvey: string | null;
  topics: ESTopic[];
  addToRef: (el: HTMLDivElement | null) => void;
}

interface StateProps {
  comparisons: MatrixResponse | null;
  scales: { [key: string]: Scale };
  singleTopic: Topic | null;
  topRow?: SegmentAndAggregations;
  selectedGroupObj: SelectedGroupObj | null;
  survey?: string;
}

type Props = OwnProps & StateProps;

export function heatmapResponseToSegmentsWithAggregations(
  response: MatrixResponse | null,
): SegmentAndAggregations[] {
  if (!response) {
    return [];
  }

  return Object.values(response.segmById).map(segment => ({
    segment,
    agg: response.aggrBySegmentId[segment.id],
  }));
}

const RatingTableInner: FC<Props> = (props) => {
  const [t] = useTranslation();
  const globalScaleId = useMemo(() => {
    if (props.selectedTopicId && props.topics.length > 0) {
      const listTopic = props.topics.find(topic => topic.id === props.selectedTopicId);
      return listTopic!.scale_version_id;
    } else if (props.singleTopic) {
      return props.singleTopic.scaleVersionId;
    }
    return null;
  }, [props.selectedTopicId, props.comparisons, props.singleTopic]);

  const hasTopics = props.comparisons && props.comparisons.valueById;

  const rows: SegmentAndAggregations[] = useMemo(() => {
    return heatmapResponseToSegmentsWithAggregations(props.comparisons);
  }, [props.comparisons]);

  if (!hasTopics) {
    return (
      <TableInner $empty={!hasTopics} ref={props.addToRef}>
        <EmptyTableInner
          messageKey={`x.matrix.table.inner.${'no_topics'}`}
          link="content/surveys"
          linkKey={`x.matrix.table.inner.${'no_topics'}.link`}
        />
      </TableInner>
    );
  }

  const allComparisons = Object.values(props.comparisons ? props.comparisons.valueById : []);
  const visibleComparisons = props.selectedTopicId
    ? allComparisons
    : props.topics
        .map(t => props.comparisons && props.comparisons.valueById[t.id])
        .filter((s): s is ESTopic => Boolean(s));

  const standardRow = (row: SegmentAndAggregations) => {
    const segment = row.segment;
    const segmentAgg = row.agg;

    const isEmpty = segment.type !== SegmentType.Anonymous && segment.user_count === 0;

    const Child = () => {
      if (isEmpty) {
        return <EmptyRow t={t} />;
      }

      if (!segmentAgg) {
        return (
          <EmptyContainer>
            {visibleComparisons.map(topic => (
              <EmptyTile key={topic.id} t={t} />
            ))}
          </EmptyContainer>
        );
      }

      return (
        <TilesRow
          topicId={props.selectedTopicId}
          segment={segment}
          scaleId={globalScaleId}
          scales={props.scales}
          globalValues={visibleComparisons}
          aggregationValues={segmentAgg.valById}
          selectedPeriod={props.selectedPeriod}
          filterSurvey={props.filterSurvey}
        />
      );
    };

    return (
      <RatingRow key={segment.id}>
        <Child />
      </RatingRow>
    );
  };

  return (
    <TableInner $empty={false} ref={props.addToRef}>
      {props.topRow && props.topRow.segment && standardRow(props.topRow)}
      {props.selectedGroupObj && (
        <MasterTilesWrapper>
          {props.selectedGroupObj && props.comparisons && (
            <TilesRow
              topicId={props.selectedTopicId}
              segment={props.selectedGroupObj.segment}
              scaleId={globalScaleId}
              scales={props.scales}
              globalValues={visibleComparisons}
              aggregationValues={props.selectedGroupObj.aggregation.valById}
              selectedPeriod={props.selectedPeriod}
            />
          )}
        </MasterTilesWrapper>
      )}

      {rows.map(standardRow)}
    </TableInner>
  );
};

const mapStateToProps = (state: StoreI): StateProps => {
  let topRow: TopRow | undefined;
  if (state.user.session.user?.userPermissions.includes(UserPermissions.ManageCompany)) {
    topRow = state.heatmap.topRow;
  }
  return {
    comparisons: state.heatmap.result,
    scales: state.scales.byId,
    singleTopic: state.topics.currentTopic,
    topRow: topRow,
    selectedGroupObj: state.heatmap.selectedGroup,
    survey: state.heatmap.survey,
  };
};

const RatingTableInnerMemo = React.memo(RatingTableInner);
const RatingTableInnerSuper = connect(mapStateToProps)(RatingTableInnerMemo);
export default RatingTableInnerSuper;
